import { faSdg8 } from '@awesome.me/kit-335a9e77a1/icons/kit/custom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartCard, InfoTooltip, LabelText, VerticalBar } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { getReferenceLines } from '../-helpers/getReferenceLines';
import { getTarget } from '../-helpers/getSDGTargetData';
import { useChartData } from '../-hooks/useChartData';

export const Sdg_DecentJobsFragment = gql(/* GraphQL */ `
  fragment Sdg_DecentJobsFragment on HealthcareIndicators {
    decentJobs_pacePct {
      average
      median
      n
      isDataSuppressed
    }
  }
`);

type DecentJobsProps = {
  className?: string;
  indicators: FragmentType<typeof Sdg_DecentJobsFragment>;
  viewerIndicators?: FragmentType<typeof Sdg_DecentJobsFragment>;
  region: string;
};

export function DecentJobs({ indicators, viewerIndicators, region, className }: DecentJobsProps) {
  const { t } = useTranslation();

  const benchmarkFragment = useFragment(Sdg_DecentJobsFragment, indicators);
  const viewerFragment = useFragment(Sdg_DecentJobsFragment, viewerIndicators);

  const benchmarkData = benchmarkFragment.decentJobs_pacePct;
  const viewerData = viewerFragment?.decentJobs_pacePct;

  const sdgTarget = useMemo(() => getTarget('decentJobs', region), [region]);
  const referenceLines = getReferenceLines(sdgTarget);

  const { groups, data, yAxisProps } = useChartData({
    viewerData,
    benchmarkData,
    referenceLineValues: referenceLines.map((x) => x.value),
  });

  const titleTooltip = t('decentJobs_titleTooltip_sdg');

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <div className="flex items-center gap-1.5">
          <FontAwesomeIcon icon={faSdg8} size="2x" className="text-sdg-8" />
          <LabelText>{t('sdgsTargeted_optionLabel_8').replace('8', '8.5')}</LabelText>
        </div>
        <ChartCard.Title>
          {t('decentJobs_title')}
          {titleTooltip && <InfoTooltip>{titleTooltip}</InfoTooltip>}
        </ChartCard.Title>
      </ChartCard.Header>
      <ChartCard.Body>
        <VerticalBar groups={groups} data={data}>
          <VerticalBar.Chart yAxis={yAxisProps} referenceLines={referenceLines} />
          <VerticalBar.Legend className="flex items-center justify-center gap-4" />
        </VerticalBar>
      </ChartCard.Body>
    </ChartCard>
  );
}
